import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, Download, Eye, ChevronLeft, ChevronRight, 
  Calendar, CheckSquare, Square, Loader2, Filter, X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 15;

function formatDate(dateString) {
  if (!dateString) return '-';
  // Parse date string manually to avoid timezone issues
  const parts = dateString.split('T')[0].split('-');
  if (parts.length >= 3) {
    const dt = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return dt.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export default function Auditor() {
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewingBill, setViewingBill] = useState(null);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filterType, setFilterType] = useState('date'); // 'date' or 'month'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [monthFrom, setMonthFrom] = useState('');
  const [monthTo, setMonthTo] = useState('');
  const [appliedFilter, setAppliedFilter] = useState(null); // { from, to, label }

  useEffect(() => {
    fetchAuditorRecords();
  }, [page, appliedFilter]);

  const fetchAuditorRecords = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/auditor/records?page=${page}&page_size=${PAGE_SIZE}`;
      if (appliedFilter) {
        if (appliedFilter.from) url += `&date_from=${appliedFilter.from}`;
        if (appliedFilter.to) url += `&date_to=${appliedFilter.to}`;
      }

      const response = await axios.get(url, { headers });
      
      const data = response.data;
      setExpenses(data.items || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.total_pages || 1);
      
    } catch (error) {
      toast.error('Failed to load auditor records');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(expenses.map(exp => exp.id)));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === expenses.length);
  };

  // Download GST bills (individual files from R2)
  const downloadBill = async (record) => {
    try {
      const token = localStorage.getItem('token');
      
      // For SVE summary invoices, use the dedicated download endpoint
      if (record.source_type === 'sve_summary_invoice' && record.source_id) {
        const response = await axios.get(
          `${API}/silica-loads/sve-summary-invoice/${record.source_id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = record.bill_filename || 'SVE_Summary.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return true;
      }
      
      // For silica load invoices, download via attachment endpoint using invoice_type
      if (record.source_type === 'silica_load' && record.invoice_type) {
        const response = await axios.get(
          `${API}/silica-loads/${record.source_id}/attachment/${record.invoice_type}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = record.bill_filename || 'invoice.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return true;
      }
      
      // For expenses - check if expense has R2-stored GST bills
      if (record.gst_bill_r2_keys?.length > 0 || record.gst_bill_filenames?.length > 0) {
        // Download each file individually
        const fileCount = record.gst_bill_filenames?.length || 1;
        for (let i = 0; i < fileCount; i++) {
          const response = await axios.get(
            `${API}/expenses/${record.id}/gst-bills/${i}`,
            { 
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            }
          );
          
          const url = URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = record.gst_bill_filenames?.[i] || `gst_bill_${i+1}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          // Small delay between downloads
          if (i < fileCount - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        return true;
      }
      
      // Fallback to regular bill_file
      if (record.bill_filename) {
        const response = await axios.get(
          `${API}/expenses/${record.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.bill_file) {
          const byteCharacters = atob(response.data.bill_file);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          
          const filename = record.bill_filename || 'bill.pdf';
          const ext = filename.split('.').pop().toLowerCase();
          let mimeType = 'application/pdf';
          if (ext === 'png') mimeType = 'image/png';
          else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
          
          const blob = new Blob([byteArray], { type: mimeType });
          const url = URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Download error:', error);
      return false;
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one record to download');
      return;
    }
    
    setDownloading(true);
    try {
      const selectedRecords = expenses.filter(r => 
        selectedIds.has(r.id) && r.bill_available
      );
      
      if (selectedRecords.length === 0) {
        toast.error('No bills attached to selected records');
        setDownloading(false);
        return;
      }
      
      let downloadedCount = 0;
      for (const record of selectedRecords) {
        const success = await downloadBill(record);
        if (success) downloadedCount++;
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      if (downloadedCount > 0) {
        toast.success(`Downloaded ${downloadedCount} bill(s)`);
      } else {
        toast.error('No bills could be downloaded');
      }
    } catch (error) {
      toast.error('Failed to download bills');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all auditor records
      const response = await axios.get(
        `${API}/auditor/records?page=1&page_size=5000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const allRecords = (response.data.items || []).filter(r => r.bill_available);
      
      if (allRecords.length === 0) {
        toast.error('No records with bills to download');
        setDownloading(false);
        return;
      }
      
      let downloadedCount = 0;
      for (const record of allRecords) {
        const success = await downloadBill(record);
        if (success) downloadedCount++;
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      if (downloadedCount > 0) {
        toast.success(`Downloaded ${downloadedCount} bill(s)`);
      } else {
        toast.error('No bills could be downloaded');
      }
    } catch (error) {
      toast.error('Failed to download bills');
    } finally {
      setDownloading(false);
    }
  };

  const handleViewBill = async (record) => {
    const hasBill = record.bill_available;
    
    if (!hasBill) {
      toast.error('No bill attached to this record');
      return;
    }
    
    // Open window immediately in click context to avoid popup blocker on mobile
    const win = window.open('', '_blank');
    
    try {
      const token = localStorage.getItem('token');
      
      // For SVE summary invoices
      if (record.source_type === 'sve_summary_invoice' && record.source_id) {
        const response = await axios.get(
          `${API}/silica-loads/sve-summary-invoice/${record.source_id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (win) { win.location.href = url; } else { window.location.href = url; }
        return;
      }
      
      // For silica load invoices — use attachment endpoint with invoice_type
      if (record.source_type === 'silica_load' && record.invoice_type) {
        const response = await axios.get(
          `${API}/silica-loads/${record.source_id}/attachment/${record.invoice_type}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (win) { win.location.href = url; } else { window.location.href = url; }
        return;
      }
      
      // For expenses with GST bills
      if (record.gst_bill_r2_keys?.length > 0 || record.gst_bill_filenames?.length > 0) {
        const response = await axios.get(
          `${API}/expenses/${record.id}/gst-bills/0`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const url = URL.createObjectURL(response.data);
        if (win) { win.location.href = url; } else { window.location.href = url; }
        
        const fileCount = record.gst_bill_filenames?.length || 1;
        if (fileCount > 1) {
          toast.info(`Viewing 1 of ${fileCount} GST bills. Use download to get all.`);
        }
        return;
      }
      
      // Fallback to regular bill (for expenses)
      const response = await axios.get(
        `${API}/expenses/${record.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.bill_file) {
        const byteCharacters = atob(response.data.bill_file);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        const filename = (record.bill_filename || '').toLowerCase();
        let mimeType = 'application/pdf';
        if (filename.endsWith('.png')) mimeType = 'image/png';
        else if (filename.endsWith('.jpg') || filename.endsWith('.jpeg')) mimeType = 'image/jpeg';
        else if (filename.endsWith('.gif')) mimeType = 'image/gif';
        
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);
        if (win) { win.location.href = url; } else { window.location.href = url; }
      } else {
        if (win) win.close();
        toast.error('Bill file not found');
      }
    } catch (error) {
      if (win) win.close();
      toast.error('Failed to load bill');
    }
  };

  const handleApplyFilter = () => {
    let from, to, label;
    if (filterType === 'date') {
      if (!dateFrom && !dateTo) { toast.error('Please select at least one date'); return; }
      if (dateFrom && dateTo && dateFrom > dateTo) { toast.error('From date must be before To date'); return; }
      from = dateFrom || undefined;
      to = dateTo || undefined;
      const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
      label = from && to ? `${fmtDate(from)} – ${fmtDate(to)}` : from ? `From ${fmtDate(from)}` : `Until ${fmtDate(to)}`;
    } else {
      if (!monthFrom && !monthTo) { toast.error('Please select at least one month'); return; }
      if (monthFrom && monthTo && monthFrom > monthTo) { toast.error('From month must be before To month'); return; }
      from = monthFrom || undefined;
      to = monthTo || undefined;
      const fmtMonth = (m) => m ? new Date(m + '-01T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '';
      label = from && to ? `${fmtMonth(from)} – ${fmtMonth(to)}` : from ? `From ${fmtMonth(from)}` : `Until ${fmtMonth(to)}`;
    }
    setAppliedFilter({ from, to, label });
    setPage(1);
    setSelectedIds(new Set());
    setSelectAll(false);
    setShowFilterDialog(false);
  };

  const handleClearFilter = () => {
    setAppliedFilter(null);
    setDateFrom('');
    setDateTo('');
    setMonthFrom('');
    setMonthTo('');
    setPage(1);
    setSelectedIds(new Set());
    setSelectAll(false);
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-amber-600 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase mb-1">GST Compliance</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Auditor
            </h1>
            <p className="text-slate-500 text-sm mt-1">Expense bills and silica load invoices for GST audit</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="filter-btn"
              variant="outline"
              onClick={() => setShowFilterDialog(true)}
              className={`rounded-xl text-sm w-full sm:w-auto ${appliedFilter ? 'border-amber-500 bg-amber-50 text-amber-700' : ''}`}
              size="sm"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
            <Button
              data-testid="download-selected-btn"
              variant="outline"
              onClick={handleDownloadSelected}
              disabled={downloading || selectedIds.size === 0}
              className="rounded-xl text-sm w-full sm:w-auto"
              size="sm"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download ({selectedIds.size})
            </Button>
            <Button
              data-testid="download-all-btn"
              onClick={handleDownloadAll}
              disabled={downloading || totalCount === 0}
              className="rounded-xl bg-slate-900 hover:bg-slate-800 text-sm w-full sm:w-auto"
              size="sm"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download All
            </Button>
          </div>

          {/* Active Filter Chip */}
          {appliedFilter && (
            <div className="flex items-center gap-2">
              <div data-testid="active-filter-chip" className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <Calendar className="w-3.5 h-3.5" />
                <span className="font-medium">{appliedFilter.label}</span>
                <button
                  data-testid="clear-filter-btn"
                  onClick={handleClearFilter}
                  className="ml-1 p-0.5 hover:bg-amber-200 rounded transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <span className="text-xs text-slate-500">{totalCount} records</span>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          <Card className="p-3 sm:p-5 border-0 rounded-xl sm:rounded-2xl shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600">
            <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm text-white/80 hidden sm:block">Total Records</span>
            </div>
            <p className="text-xs text-white/80 sm:hidden mb-1">Total</p>
            <p className="text-lg sm:text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {totalCount}
            </p>
          </Card>
          
          <Card className="p-3 sm:p-5 border-0 rounded-xl sm:rounded-2xl shadow-lg bg-gradient-to-br from-amber-500 to-amber-600">
            <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm text-white/80 hidden sm:block">Selected</span>
            </div>
            <p className="text-xs text-white/80 sm:hidden mb-1">Selected</p>
            <p className="text-lg sm:text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {selectedIds.size}
            </p>
          </Card>
        </div>

        {/* Expenses Table */}
        <Card className="border-0 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                data-testid="select-all-checkbox"
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 hover:text-slate-900"
              >
                {selectAll ? (
                  <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                ) : (
                  <Square className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
                Select All
              </button>
              <span className="text-xs sm:text-sm text-slate-400">|</span>
              <span className="text-xs sm:text-sm text-slate-500">
                {selectedIds.size}/{expenses.length}
              </span>
            </div>
            <span className="text-xs sm:text-sm text-slate-500">
              {expenses.length} of {totalCount} records
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No records found</p>
              <p className="text-sm">GST expenses and silica load invoices will appear here</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="sm:hidden divide-y divide-slate-100">
                {expenses.map((record) => (
                  <div 
                    key={record.id} 
                    className={`p-3 ${selectedIds.has(record.id) ? 'bg-emerald-50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        data-testid={`select-expense-mobile-${record.id}`}
                        onClick={() => handleSelectOne(record.id)}
                        className="mt-1"
                      >
                        {selectedIds.has(record.id) ? (
                          <CheckSquare className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Square className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <span className="text-sm font-medium text-slate-900">{record.category}</span>
                            {record.sub_category && (
                              <span className="text-xs text-slate-500 ml-1">({record.sub_category})</span>
                            )}
                            {!record.sub_category && record.client_name && (
                              <span className="text-xs text-slate-500 ml-1">({record.client_name.split(' ')[0]})</span>
                            )}
                          </div>
                          <span className="text-xs text-slate-500">{formatDate(record.date)}</span>
                        </div>
                        {record.invoice_number && (
                          <p className="text-xs text-slate-500 mt-0.5 font-mono">{record.invoice_number}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {record.bill_available ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewBill(record)}
                              className="h-7 px-2 text-xs"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View Bill
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400">No bill</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase w-10"></th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Invoice No.</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase">Bill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((record) => (
                      <tr 
                        key={record.id} 
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                          selectedIds.has(record.id) ? 'bg-emerald-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <button
                            data-testid={`select-expense-${record.id}`}
                            onClick={() => handleSelectOne(record.id)}
                            className="p-1"
                          >
                            {selectedIds.has(record.id) ? (
                              <CheckSquare className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <Square className="w-5 h-5 text-slate-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-900">{formatDate(record.date)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{record.category}</p>
                            {record.client_name && (
                              <p className="text-xs text-slate-500">{record.client_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700 font-mono">{record.invoice_number || '-'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {record.bill_available ? (
                            <Button
                              data-testid={`view-bill-${record.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewBill(record)}
                              className="h-8 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400">No bill</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 w-8 p-0 rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-1 text-slate-400 text-xs">…</span>
                    ) : (
                      <Button
                        key={item}
                        variant={item === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(item)}
                        className={`h-8 w-8 p-0 rounded-lg text-xs ${item === page ? 'bg-slate-900 text-white' : ''}`}
                      >
                        {item}
                      </Button>
                    )
                  )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 w-8 p-0 rounded-lg"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Info Card */}
        <Card className="p-3 sm:p-4 border-0 rounded-xl bg-blue-50">
          <div className="flex items-start gap-2 sm:gap-3">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs sm:text-sm text-blue-800">
              <p className="font-medium mb-1">Auditor View</p>
              <p className="hidden sm:block">This view shows all expenses where GST Filing is marked as "Yes". These expenses have associated bills uploaded for GST compliance and audit purposes. Use the download buttons to export data for external auditing.</p>
              <p className="sm:hidden">Expenses with GST Filing = Yes. Download bills for audit.</p>
            </div>
          </div>
        </Card>

        {/* Filter Dialog */}
        <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-slate-900">Filter by Date Range</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              {/* Toggle: Date vs Month */}
              <div className="flex gap-2">
                <button
                  data-testid="filter-type-date"
                  onClick={() => setFilterType('date')}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    filterType === 'date'
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Exact Dates
                </button>
                <button
                  data-testid="filter-type-month"
                  onClick={() => setFilterType('month')}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    filterType === 'month'
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Month Range
                </button>
              </div>

              {filterType === 'date' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">From Date</Label>
                    <input
                      data-testid="filter-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">To Date</Label>
                    <input
                      data-testid="filter-date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">From Month</Label>
                    <input
                      data-testid="filter-month-from"
                      type="month"
                      value={monthFrom}
                      onChange={(e) => setMonthFrom(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">To Month</Label>
                    <input
                      data-testid="filter-month-to"
                      type="month"
                      value={monthTo}
                      onChange={(e) => setMonthTo(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {appliedFilter && (
                  <Button
                    data-testid="filter-clear-btn"
                    variant="outline"
                    onClick={() => { handleClearFilter(); setShowFilterDialog(false); }}
                    className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Clear Filter
                  </Button>
                )}
                <Button
                  data-testid="filter-apply-btn"
                  onClick={handleApplyFilter}
                  className="flex-1 rounded-xl bg-slate-900 hover:bg-slate-800"
                >
                  Apply Filter
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
